import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { TrialTruncatedText } from '@/modules/trials/components/TrialTruncatedText';
import type { SubscriptionEventHistoryItem } from '../types';
import { formatDateTime } from '../utils/helpers';
import { SubscriptionEventSourceBadge } from './SubscriptionEventSourceBadge';
import { SubscriptionEventTypeBadge } from './SubscriptionEventTypeBadge';

interface SubscriptionEventMobileCardProps {
  event: SubscriptionEventHistoryItem;
  locale: string;
  productLabel: string | null;
  onView: () => void;
}

export function SubscriptionEventMobileCard({
  event,
  locale,
  productLabel,
  onView,
}: SubscriptionEventMobileCardProps) {
  const { t } = useTranslation();
  const unavailable = t('subscriptionEvents.common.unavailable');

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1 min-w-0">
          <p className="text-xs text-muted-foreground tabular-nums">
            {formatDateTime(event.occurredAt, locale)}
          </p>
          <SubscriptionEventTypeBadge eventType={event.eventType} />
        </div>
        <SubscriptionEventSourceBadge eventSource={event.eventSource} />
      </div>

      <div className="grid grid-cols-1 gap-2 text-sm">
        <div>
          <p className="text-xs text-muted-foreground">{t('subscriptionEvents.columns.tenant')}</p>
          <TrialTruncatedText
            value={event.tenantId}
            fallback={unavailable}
            monospace
            maxWidthClass="max-w-full"
          />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t('subscriptionEvents.columns.product')}</p>
          <TrialTruncatedText
            value={productLabel ?? event.productId}
            fallback={unavailable}
            maxWidthClass="max-w-full"
          />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t('subscriptionEvents.columns.status')}</p>
          <TrialTruncatedText
            value={event.eventStatus}
            fallback={unavailable}
            maxWidthClass="max-w-full"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onView}>
          <Icons.Eye className="h-3.5 w-3.5" />
          {t('common.view')}
        </Button>
      </div>
    </div>
  );
}
